import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DEFAULT_INTERIM_TAGS = [
  "Graphic Accepted", "Billing Approved", "PDF Sent", "Edits",
  "PDF Approved", "Pilot Scheduled", "Pilot Sent", "Pilot Approved",
];

export const InterimTagEditorV53 = ({ value = "", disabled = false, isAdmin = false, onChange }) => {
  const [tags, setTags] = useState(DEFAULT_INTERIM_TAGS);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState(false);
  const [managedTags, setManagedTags] = useState([]);
  const [savingName, setSavingName] = useState("");
  const reloadTags = async () => {
    const { data } = await axios.get(`${API}/workflow/interim-tags`);
    setTags([...new Set([...DEFAULT_INTERIM_TAGS, ...(data.tags || [])])]);
  };
  const manageTags = async () => {
    setManaging((before) => !before);
    if (!managing) {
      try {
        const { data } = await axios.get(`${API}/admin/interim-tags`);
        setManagedTags(data.tags || []);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Could not load custom tags");
      }
    }
  };
  const setActive = async (tag) => {
    setSavingName(tag.name);
    try {
      await axios.put(`${API}/admin/interim-tags/${encodeURIComponent(tag.name)}/active`, { active: !tag.active });
      setManagedTags((prev) => prev.map((item) => item.name === tag.name ? { ...item, active: !tag.active } : item));
      await reloadTags();
      toast.success(tag.active ? "Tag retired; existing projects unchanged" : "Tag reactivated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update custom tag");
    } finally {
      setSavingName("");
    }
  };
  useEffect(() => {
    let active = true;
    axios.get(`${API}/workflow/interim-tags`).then(({ data }) => {
      if (active) setTags([...new Set([...DEFAULT_INTERIM_TAGS, ...(data.tags || [])])]);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const createTag = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/workflow/interim-tags`, { name: trimmed });
      setTags((previous) => [...new Set([...previous, data.name])]);
      if (managing) setManagedTags((previous) => [...previous, { name: data.name, active: true }]);
      await onChange(data.name);
      setName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create interim tag");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-2">
      <select aria-label="Interim tag" value={value} disabled={disabled || creating}
        onChange={(event) => onChange(event.target.value)}
        className="w-full min-w-40 rounded-md border border-border bg-secondary px-2 py-1 text-xs">
        <option value="">No tag</option>
        {[...new Set([...tags, ...(value ? [value] : [])])].map((tag) => <option key={tag} value={tag}>{tag}</option>)}
      </select>
      <div className="flex gap-1">
        <input aria-label="New interim tag" maxLength={60} value={name}
          disabled={disabled || creating} placeholder="New tag…"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); createTag(); } }}
          className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xs" />
        <button type="button" disabled={disabled || creating || !name.trim()} onClick={createTag}
          className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">
          {creating ? "Adding…" : "Add & Apply"}
        </button>
      </div>
      {isAdmin && <div className="border-t border-border pt-1">
        <button type="button" onClick={manageTags} className="text-xs underline">
          {managing ? "Hide custom tag management" : "Manage custom tags"}
        </button>
        {managing && <div className="mt-1 max-h-48 space-y-1 overflow-y-auto">
          {managedTags.length === 0 && <p className="text-xs text-muted-foreground">No custom tags created yet</p>}
          {managedTags.map((tag) => <div key={tag.name} className="flex items-center justify-between gap-1 text-xs">
            <span title={tag.name} className="truncate">{tag.name}{!tag.active ? " (retired)" : ""}</span>
            <button type="button" disabled={Boolean(savingName) || disabled}
              onClick={() => setActive(tag)} className="rounded border border-border px-1 py-0.5 disabled:opacity-50">
              {savingName === tag.name ? "Saving…" : tag.active ? "Retire" : "Restore"}
            </button>
          </div>)}
        </div>}
      </div>}
    </div>
  );
};
